import { Injectable } from '@angular/core';
import { DossierService } from './dossier.service';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { DossierRequest, DossierResponse } from '../model/dossier.model';
import { catchError } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { DOSSIER_ERROR_KEYS, ERROR_DIALOG_KEYS, getDialogProperties } from '../constants/dossier.constants';
import { EMPTY } from 'rxjs';
import { DossierResultComponent } from '../components/modal/dossier-result.component';

@Injectable({
  providedIn: 'root',
})
export class DossierFlowService {
  constructor(
    private readonly dossierService: DossierService,
    private readonly translateService: TranslateService,
    private readonly toastrSrv: ToastrService,
    private readonly modalService: NgbModal
  ) { }

  executeDossierFlow({
    mailbox,
    urlPGA,
    urlWebmail,
    salesChannel
  }: {
    mailbox: string;
    urlPGA: string;
    urlWebmail: string;
    salesChannel: string;
  }) {
    const dossierRequest = new DossierRequest(mailbox, 'PGA', urlPGA, urlWebmail);
    this.dossierService
      .query(dossierRequest)
      .pipe(
        catchError((resError: HttpErrorResponse) => {
          if (this.isNotServerError(resError)) {
            this.handleDossierError(resError.error.errorKey, salesChannel);
          }
          return EMPTY;
        })
      )
      .subscribe((res: DossierResponse) => {
        this.redirectToIds(res.redirectToIDS);
      });
  }

  private isNotServerError(resError: HttpErrorResponse) {
    return resError.status !== 0 && !resError.status.toString().startsWith('5');
  }

  private handleDossierError(errorKey: string, salesChannel: string) {
    if (ERROR_DIALOG_KEYS.has(errorKey)) {
      this.showErrorDialog(getDialogProperties(salesChannel, errorKey));
      return;
    }

    if (errorKey === DOSSIER_ERROR_KEYS.DOSSIER_ALREADY_EXIST) {
      this.handleDossierAlreadyExistError();
      return;
    }

    this.handleDefaultError(errorKey);
  }

  private showErrorDialog({ title, description, btnVisible }: { title: string; description: string; btnVisible: boolean }) {
    this.showDialog(this.translateService.instant(title), this.translateService.instant(description), btnVisible);
  }

  /**
   * Show Dossier Result modal
   * @param title Modal title
   * @param message Modal message
   * @param btnVisible Flag to show/hide Confirm button
   */
  private showDialog(title: string, message: string, btnVisible: boolean): void {
    const modalRef: NgbModalRef = this.modalService.open(DossierResultComponent, { size: 'lg' });
    const compInstance = modalRef.componentInstance as DossierResultComponent;
    compInstance.title = title;
    compInstance.message = message;
    compInstance.btnVisible = btnVisible;
    compInstance.btnText = this.translateService.instant('contacts.dossier.linkGuide');
    compInstance.btnClose = this.translateService.instant('contacts.dossier.close');

    modalRef.result.finally(() => { });
  }

  private handleDossierAlreadyExistError() {
    window.location.reload();
  }

  private handleDefaultError(errorKey: string) {
    this.toastrSrv.error(this.translateService.instant(`contacts.${errorKey}`));
  }

  private redirectToIds(redirectToIDS = '') {
    window.open(redirectToIDS, '_self');
  }
}
